import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function Profile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Password State
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [changing, setChanging] = useState(false);

    // Delete Account State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Export State
    const [exporting, setExporting] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProfile = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/user`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfile(res.data);
        } catch (err) {
            toast.error('Failed to load profile');
            console.error(err);
        }
        setLoading(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!oldPassword || !newPassword || !confirmPassword) return toast.error('Please fill in all fields');
        if (newPassword !== confirmPassword) return toast.error('New passwords do not match');
        if (newPassword.length < 8) return toast.error('Password must be at least 8 characters');

        setChanging(true);
        try {
            await axios.put(`${API_URL}/api/user/password`, { oldPassword, newPassword }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Password changed successfully!');
            setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to change password');
        }
        setChanging(false);
    };

    const handleExportData = async () => {
        setExporting(true);
        try {
            const response = await axios.get(`${API_URL}/api/user/data-export`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'infloir-data-export.json');
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Data exported successfully!');
        } catch (err) {
            toast.error('Failed to export data');
        }
        setExporting(false);
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        if (!deletePassword) return toast.error('Please enter your password');

        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/user/account`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { password: deletePassword }
            });
            toast.success('Account deleted permanently.');
            localStorage.clear();
            navigate('/');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete account. Check your password.');
            setDeleting(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    if (loading) {
        return (
            <div className="dashboard-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
            </div>
        );
    }

    if (!profile) return null;

    return (
        <div className="dashboard-layout">
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    {role === 'WORKER' && (
                        <>
                            <Link to="/worker-dashboard" className="nav-item">
                                <span>📊</span> Dashboard
                            </Link>
                            {/* RE-ADDED REPUTATION LINK */}
                            <Link to="/worker/reputation" className={`nav-item ${location.pathname === '/worker/reputation' ? 'active' : ''}`}>
                                <span>🏆</span> Reputation
                            </Link>
                            <Link to="/wallet" className="nav-item">
                                <span>💰</span> Wallet
                            </Link>
                        </>
                    )}
                    {role === 'BUSINESS' && (
                        <Link to="/business-dashboard" className="nav-item">
                            <span>🏢</span> Dashboard
                        </Link>
                    )}
                    <Link to="/profile" className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}>
                        <span>👤</span> Profile
                    </Link>
                </nav>
                
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>My Profile</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{profile.email}</span>
                </header>

                <main className="page-container page-fade-in">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                        
                        {/* LEFT COLUMN: PROFILE INFO */}
                        <div className="card">
                            <h3 style={{ fontSize: '16px', marginBottom: '24px', color: 'var(--text)' }}>Account Details</h3>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Role</span>
                                <span className="badge">{profile.role}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Email Status</span>
                                <span className={`badge ${profile.emailVerified ? 'badge-success' : 'badge-error'}`}>
                                    {profile.emailVerified ? 'Verified' : 'Not Verified'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Joined</span>
                                <span style={{ color: 'var(--text)', fontSize: '14px' }}>{new Date(profile.createdAt).toLocaleDateString()}</span>
                            </div>
                            
                            <button onClick={handleExportData} className="btn-secondary" style={{ width: '100%', marginTop: '24px' }} disabled={exporting}>
                                {exporting ? 'Exporting...' : '📥 Export My Data (JSON)'}
                            </button>
                        </div>

                        {/* MIDDLE COLUMN: CHANGE PASSWORD */}
                        <div className="card">
                            <h3 style={{ fontSize: '16px', marginBottom: '24px', color: 'var(--text)' }}>Security</h3>
                            
                            <form onSubmit={handleChangePassword}>
                                <div className="form-field">
                                    <label className="form-label">Current Password</label>
                                    <input type={showPassword ? 'text' : 'password'} placeholder="Enter current password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">New Password</label>
                                    <input type={showPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Confirm New Password</label>
                                    <input type={showPassword ? 'text' : 'password'} placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-8px', marginBottom: '20px' }}>
                                    <input type="checkbox" id="showPwd" checked={showPassword} onChange={() => setShowPassword(!showPassword)} style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
                                    <label htmlFor="showPwd" style={{ fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}>Show passwords</label>
                                </div>

                                <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={changing}>
                                    {changing ? 'Updating...' : 'Update Password'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* DANGER ZONE */}
                    <div className="card" style={{ border: '1px solid var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '8px', color: 'var(--error)' }}>Danger Zone</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                            Once you delete your account, there is no going back. All your data, campaigns, and transaction history will be permanently anonymized.
                        </p>
                        <button onClick={() => setShowDeleteModal(true)} className="btn-danger" style={{ padding: '10px 20px' }}>
                            Delete Account
                        </button>
                    </div>

                </main>
            </div>

            {/* DELETE ACCOUNT MODAL */}
            {showDeleteModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={() => setShowDeleteModal(false)}>
                    <div className="card" style={{ width: '100%', maxWidth: '450px', padding: '32px', border: '1px solid var(--error)' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: 'var(--error)' }}>Delete Account</h3>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            This action cannot be undone. To confirm, please enter your password.
                        </p>

                        <form onSubmit={handleDeleteAccount}>
                            <div className="form-field">
                                <label className="form-label">Password</label>
                                <input type="password" placeholder="Enter your password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} required />
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                                <button type="button" onClick={() => setShowDeleteModal(false)} className="btn-secondary" disabled={deleting}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-danger" disabled={deleting}>
                                    {deleting ? 'Deleting...' : 'Permanently Delete'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profile;